import React, { useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { theme, Avatar } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { useBand } from '../api/queries';
import { PoweredBySpec } from '../components/PoweredBySpec/PoweredBySpec';
import { LinkIcon, BandGroupIcon } from '../components/icons';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import './ShowQR.css';

const QR_SIZE = 153;
const LOGO_CIRCLE_SIZE = 48;

// Black sunglasses logo with white circle background (no border) as SVG data URL
const SUNGLASS_LOGO_WITH_CIRCLE_SVG = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="24" cy="24" r="34" fill="white"/>
  <g transform="translate(1, 16)">
    <path d="M44.5795 0.997093C43.2008 0.687509 39.1804 -0.126052 35.58 0.0166421C31.1509 0.192199 27.1821 1.02533 25.2743 1.55134C24.2436 1.9241 22.6283 1.85848 22.6283 1.85848H22.4458C22.4458 1.85848 20.8305 1.9241 19.7998 1.55134C17.8923 1.02501 13.9235 0.19186 9.49403 0.0166421C5.89345 -0.126114 1.873 0.687821 0.494577 0.997093C0.203837 1.06236 0 1.31925 0 1.61732V3.66435C0 3.92438 0.158456 4.15822 0.399641 4.25491L0.46491 4.28109C0.649894 4.35508 0.779033 4.52157 0.801371 4.71946C0.968208 6.19617 1.77065 12.3925 3.64006 14.0504C5.76706 15.9362 8.76837 15.9118 10.525 15.9362C12.2813 15.9603 17.1447 16.8782 20.7084 6.68653C20.7733 6.50085 20.825 6.30784 20.8759 6.11796C20.9796 5.73159 21.2965 4.9505 22.168 4.88447H22.9062C23.7777 4.95009 24.0943 5.73122 24.1983 6.11796C24.2493 6.30784 24.3009 6.50085 24.3659 6.68653C27.9298 16.8779 32.7931 15.9603 34.5492 15.9362C36.3062 15.9121 39.3072 15.9362 41.4342 14.0504C43.3036 12.3932 44.1056 6.19662 44.2729 4.71946C44.2952 4.52157 44.4243 4.35508 44.6093 4.28109L44.6746 4.25491C44.9161 4.15823 45.0742 3.92438 45.0742 3.66435V1.61732C45.0735 1.31925 44.8693 1.06236 44.5786 0.997093H44.5795ZM4.13019 11.9819C3.95672 11.6957 3.81257 11.3879 3.70333 11.0615C3.45727 10.3279 3.18293 9.41693 2.97142 8.46793L7.98974 1.55509C8.56703 1.50134 9.10699 1.48284 9.55866 1.48284C10.3356 1.48284 10.8557 1.5338 10.8717 1.53555L10.8787 1.53589C11.1506 1.55614 11.4109 1.57918 11.6643 1.60396L4.13019 11.9819ZM28.5445 11.9261C28.0203 10.9942 27.5794 9.83822 27.2385 8.61456L32.2317 1.73656C32.8191 1.65803 33.4554 1.58753 34.1619 1.53483L34.1692 1.53448C34.1852 1.53273 34.7049 1.48177 35.4818 1.48177C35.6783 1.48177 35.8927 1.48527 36.1185 1.49364L28.5445 11.9261Z" fill="black"/>
  </g>
</svg>`;

const SUNGLASS_LOGO_DATA_URL = `data:image/svg+xml;base64,${btoa(SUNGLASS_LOGO_WITH_CIRCLE_SVG)}`;

const copyToClipboard = (text: string) => {
    return navigator.clipboard.writeText(text).catch(() => {
        console.log('Copy failed, URL:', text);
    });
};

const BandQR: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { token } = theme.useToken();
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const qrRef = useRef<HTMLDivElement>(null);

    const { data: band, isLoading, error } = useBand(id);

    // Build band URL (without /qr suffix for scanning)
    const bandUrl = `${window.location.origin}/bands/${id}`;

    const handleCopyLink = async () => {
        await copyToClipboard(bandUrl);
        setToastMessage('Link Copied');
    };

    const handleDownloadQR = useCallback(() => {
        const svgElement = qrRef.current?.querySelector('svg');
        if (!svgElement) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scale = 3;
        const size = QR_SIZE * scale;
        const logoSize = LOGO_CIRCLE_SIZE * scale;
        canvas.width = size;
        canvas.height = size;

        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);

        const qrImg = new Image();
        const logoImg = new Image();

        let imagesLoaded = 0;
        const onImageLoad = () => {
            imagesLoaded++;
            if (imagesLoaded < 2) return;

            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, size, size);
            ctx.drawImage(qrImg, 0, 0, size, size);

            const centerX = size / 2;
            const centerY = size / 2;
            ctx.drawImage(
                logoImg,
                centerX - logoSize / 2,
                centerY - logoSize / 2,
                logoSize,
                logoSize
            );

            const pngUrl = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            downloadLink.download = `band-${id}-qr.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            URL.revokeObjectURL(svgUrl);
            setToastMessage('QR Code Downloaded');
        };

        qrImg.onload = onImageLoad;
        logoImg.onload = onImageLoad;
        logoImg.onerror = () => {
            imagesLoaded++;
            onImageLoad();
        };

        qrImg.src = svgUrl;
        logoImg.src = SUNGLASS_LOGO_DATA_URL;
    }, [id]);

    if (isLoading) {
        return (
            <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                Loading band...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorError }}>
                Error loading band: {error.message}
            </div>
        );
    }

    if (!band) {
        return (
            <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                Band not found.
            </div>
        );
    }

    return (
        <div className="show-qr-page">
            <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />

            {/* Band Info */}
            <div className="show-qr-page__band-section">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        size={80}
                        src={band.photo_url}
                        icon={!band.photo_url && <BandGroupIcon size={48} />}
                        style={{ backgroundColor: band.photo_url ? token.colorBgContainer : '#404040' }}
                        shape="square"
                    />
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 600, color: token.colorText }}>{band.name}</div>
                        {(band.city || band.state) && (
                            <div style={{ fontSize: 14, color: token.colorTextSecondary, marginTop: 4 }}>
                                {[band.city, band.state].filter(Boolean).join(', ')}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* QR Code */}
            <div className="show-qr-page__qr-section">
                <div className="show-qr-page__qr-container" ref={qrRef}>
                    <QRCodeSVG
                        value={bandUrl}
                        size={QR_SIZE}
                        level="H"
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        imageSettings={{
                            src: SUNGLASS_LOGO_DATA_URL,
                            x: undefined,
                            y: undefined,
                            height: LOGO_CIRCLE_SIZE,
                            width: LOGO_CIRCLE_SIZE,
                            excavate: true,
                        }}
                    />
                    <button className="qr-badge" onClick={handleDownloadQR} aria-label="Download QR code">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="2" width="28" height="28" rx="14" fill="#0A84FF" stroke="black" strokeWidth="4"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M10.3999 21.5996C10.3999 21.3874 10.4842 21.184 10.6342 21.0339C10.7842 20.8839 10.9877 20.7996 11.1999 20.7996H20.7999C21.0121 20.7996 21.2156 20.8839 21.3656 21.0339C21.5156 21.184 21.5999 21.3874 21.5999 21.5996C21.5999 21.8118 21.5156 22.0153 21.3656 22.1653C21.2156 22.3153 21.0121 22.3996 20.7999 22.3996H11.1999C10.9877 22.3996 10.7842 22.3153 10.6342 22.1653C10.4842 22.0153 10.3999 21.8118 10.3999 21.5996ZM13.0343 15.434C13.1843 15.284 13.3878 15.1998 13.5999 15.1998C13.812 15.1998 14.0155 15.284 14.1655 15.434L15.1999 16.4684V10.3996C15.1999 10.1874 15.2842 9.98395 15.4342 9.83392C15.5842 9.68389 15.7877 9.59961 15.9999 9.59961C16.2121 9.59961 16.4156 9.68389 16.5656 9.83392C16.7156 9.98395 16.7999 10.1874 16.7999 10.3996V16.4684L17.8343 15.434C17.9081 15.3576 17.9964 15.2967 18.094 15.2547C18.1916 15.2128 18.2966 15.1907 18.4028 15.1898C18.509 15.1889 18.6143 15.2091 18.7127 15.2494C18.811 15.2896 18.9003 15.349 18.9754 15.4241C19.0505 15.4992 19.1099 15.5885 19.1502 15.6868C19.1904 15.7852 19.2106 15.8905 19.2097 15.9967C19.2088 16.103 19.1867 16.2079 19.1448 16.3055C19.1029 16.4031 19.0419 16.4914 18.9655 16.5652L16.5655 18.9652C16.4155 19.1152 16.212 19.1994 15.9999 19.1994C15.7878 19.1994 15.5843 19.1152 15.4343 18.9652L13.0343 16.5652C12.8843 16.4152 12.8001 16.2117 12.8001 15.9996C12.8001 15.7875 12.8843 15.584 13.0343 15.434V15.434Z" fill="white"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Copy Link */}
            <button className="show-qr-page__copy-link" onClick={handleCopyLink}>
                <LinkIcon size="s" tone="lightGrey" />
                <span>Copy Band Link</span>
            </button>

            {/* Powered by Spec */}
            <div className="show-qr-page__powered-by">
                <PoweredBySpec />
            </div>
        </div>
    );
};

export default BandQR;
